import { usePathname, useSearchParams } from 'next/navigation'
import { useCallback, useMemo } from 'react'

import { ColorGroup } from '../../../../../../types/graphql'
import { ProductDetailsContainer } from '../../contexts/ProductDetailsContainer'
import { useProductPagePortal } from '../../hooks/useProductPagePortal'
import { GarmentAttributes, PortalGarmentAttributes } from '../GarmentAttributes'

import * as Styles from './styles'

/**
 * The active tab is either "figs" or "portals"
 * "figs" is the default tab that displays the regular garment attributes
 * "portals" is the tab that displays the portal-specific garment attributes
 */
type TabKeys = 'figs' | 'portals'

/**
 * Tabbed section that displays the portal-specific & regular garment attributes.
 *
 * Displayed to portal users when they are visiting a product page that contains a product color/variant
 * that is included in their portal offering.
 */
export default function GarmentAttributeTabs() {
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const { colorGroups, portalColorGroups, selectedColorKey } =
    ProductDetailsContainer.useContainer()

  const { isPortalColorSelected } = useProductPagePortal({
    portalColorGroups,
    selectedColorKey,
  })

  // Derive the current active tab depending on the current selected color
  const activeTab = useMemo<TabKeys>(() => {
    if (isPortalColorSelected) return 'portals'
    return 'figs'
  }, [isPortalColorSelected])

  // Generate the URL to use for a given tab
  // This will navigate the user to the current PDP url with the first color in the given color group selected
  const generateTabLink = useCallback(
    (cgs: ColorGroup[]) => {
      const colorKey = cgs[0]?.colors?.[0]?.rawName
      const search = new URLSearchParams(searchParams?.toString() ?? '')
      if (colorKey) search.set('color', colorKey)

      return {
        pathname,
        search: search.toString(),
      }
    },
    [searchParams, pathname]
  )

  return (
    <>
      <Styles.TabContainer>
        {colorGroups.length > 0 && (
          <Styles.Tab
            aria-label='Show FIGS offerings'
            aria-current={activeTab === 'figs'}
            href={generateTabLink(colorGroups) ?? ''}
            scroll={false}
          >
            <Styles.TabText>FIGS</Styles.TabText>
          </Styles.Tab>
        )}
        {portalColorGroups.length > 0 && (
          <Styles.Tab
            aria-label='Show your organizations offerings'
            aria-current={activeTab === 'portals'}
            href={generateTabLink(portalColorGroups) ?? ''}
            scroll={false}
          >
            <Styles.TabText>My Uniform</Styles.TabText>
          </Styles.Tab>
        )}
      </Styles.TabContainer>
      {activeTab === 'figs' && <GarmentAttributes />}
      {activeTab === 'portals' && <PortalGarmentAttributes />}
    </>
  )
}
